import React from 'react';
import { Drawer, IconButton, Button, CircularProgress, withStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useShipmentRouteHistory } from '../../hooks';
import { DRAWER_WIDTH } from '../../constants';
import TimelineEntry from './TimelineEntry';
import type { ShipmentRouteHistoryItem } from '../../hooks/useShipmentRouteHistory';

const styles = (theme: any) => ({
  drawerPaper: {
    width: DRAWER_WIDTH,
    backgroundColor: theme.ModalBGColor,
    boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
    zIndex: 1400
  },
  drawerContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%'
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${theme.AppBGColor}`,
    backgroundColor: theme.ModalBGColor
  },
  drawerHeaderTitle: {
    display: 'flex',
    alignItems: 'center'
  },
  drawerTitle: {
    fontWeight: 600,
    fontSize: 14,
    color: theme.HeaderSelectedText
  },
  closeButton: {
    padding: 4,
    color: theme.iconColor,
    '&:hover': {
      color: theme.HeaderSelectedText
    }
  },
  drawerContent: {
    flex: 1,
    overflow: 'auto',
    padding: '20px'
  },
  summaryText: {
    fontSize: 14,
    color: theme.gridTextColor,
    marginBottom: 24
  },
  summaryCounts: {
    display: 'flex',
    marginBottom: 32,
    paddingBottom: 20
  },
  summaryCountItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    marginRight: 48,
    '&:last-child': {
      marginRight: 0
    }
  },
  summaryCountValue: {
    fontSize: 20,
    fontWeight: 600,
    color: theme.TextColor,
    lineHeight: 1.2
  },
  summaryCountLabel: {
    fontSize: 14,
    color: theme.gridTextColor,
    marginTop: 4
  },
  timeline: {
    position: 'relative' as const
  },
  drawerFooter: {
    padding: '16px 20px',
    borderTop: `1px solid ${theme.AppBGColor}`,
    display: 'flex',
    justifyContent: 'flex-start'
  },
  closeButtonFooter: {
    textTransform: 'none' as const,
    minWidth: 80
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200
  },
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
    color: theme.ErrorMessageColor,
    fontSize: 14
  }
});

interface SummaryContentProps {
  classes: Record<string, string>;
  numberOfRoutes: number;
  numberOfPlanners: number;
  shipmentRouteHistory: ShipmentRouteHistoryItem[];
  loading: boolean;
  error: string | null;
}

const SummaryContent: React.FC<SummaryContentProps> = ({
  classes,
  numberOfRoutes,
  numberOfPlanners,
  shipmentRouteHistory,
  loading,
  error
}) => {
  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.errorContainer}>
        {error}
      </div>
    );
  }

  return (
    <>
      <div className={classes.summaryText}>
        This pro was part of {numberOfRoutes} {numberOfRoutes === 1 ? 'route' : 'routes'}
      </div>

      <div className={classes.summaryCounts}>
        <div className={classes.summaryCountItem}>
          <span className={classes.summaryCountValue}>{numberOfRoutes}</span>
          <span className={classes.summaryCountLabel}>Routes Involved</span>
        </div>
        <div className={classes.summaryCountItem}>
          <span className={classes.summaryCountValue}>{numberOfPlanners}</span>
          <span className={classes.summaryCountLabel}>Planners Involved</span>
        </div>
      </div>

      <div className={classes.timeline}>
        {shipmentRouteHistory.map((historyItem, index) => (
          <TimelineEntry
            key={historyItem.routeId}
            historyItem={historyItem}
            isLastItem={index === shipmentRouteHistory.length - 1}
          />
        ))}
      </div>
    </>
  );
};

interface MovementSummaryProps {
  classes: Record<string, string>;
  open: boolean;
  onClose: () => void;
  proId: string | null;
  planDate?: string;
  terminalCode?: string;
  scenarioId?: string;
}

const MovementSummary: React.FC<MovementSummaryProps> = ({
  classes,
  open,
  onClose,
  proId,
  planDate,
  terminalCode,
  scenarioId
}) => {
  const {
    numberOfPlanners,
    numberOfRoutes,
    shipmentRouteHistory,
    loading,
    error
  } = useShipmentRouteHistory({
    planDate,
    terminalCode,
    scenarioId,
    proId,
    enabled: open && Boolean(proId)
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.drawerPaper
      }}
      ModalProps={{
        disableEnforceFocus: true,
        disableBackdropClick: true
      }}
    >
      <div className={classes.drawerContainer}>
        <div className={classes.drawerHeader}>
          <div className={classes.drawerHeaderTitle}>
            <span className={classes.drawerTitle}>Movement Summary | Pro# {proId}</span>
          </div>
          <IconButton
            className={classes.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </div>

        <div className={`${classes.drawerContent} customScroll`}>
          <SummaryContent
            classes={classes}
            numberOfRoutes={numberOfRoutes}
            numberOfPlanners={numberOfPlanners}
            shipmentRouteHistory={shipmentRouteHistory}
            loading={loading}
            error={error}
          />
        </div>

        <div className={classes.drawerFooter}>
          <Button
            variant="outlined"
            color="primary"
            onClick={onClose}
            className={classes.closeButtonFooter}
          >
            Close
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default withStyles(styles)(MovementSummary);
